import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
// Folga do "já está na hora". Na carona do ping (de 3 em 3 s) a batida sai até
// 3 s depois de vencer, e a presença no servidor vale PRESENCE_DURATION (20 s
// por padrão, comparação estrita): vencer aos 15 s faz a batida sair entre 15 e
// 18 s, antes de a presença cair. Também cobre o timer de 20 s disparar 1 ms
// antes da conta fechar (sem folga a batida pularia pra 40 s).
const PRESENCE_DUE_SLACK = 5000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          this.isSubscribed = true;
        },
        disconnected: () => {
          this.isSubscribed = false;
          this.handleDisconnected();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.lastPresenceAt = 0;
    // Só bate com a assinatura confirmada pelo servidor: o welcome da conexão
    // passa pelo recordPing (recordConnect) ANTES do subscribe, e a batida ali
    // ia pra um canal que o servidor ainda não conhece ("Unable to find
    // subscription") — perdida, mas marcada como feita. A própria assinatura
    // já registra a presença no servidor (RoomChannel#subscribed).
    this.isSubscribed = false;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.beatPresenceIfDue();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  sendPresence() {
    this.lastPresenceAt = Date.now();
    this.subscription.updatePresence();
  }

  beatPresenceIfDue() {
    if (!this.isSubscribed) return;
    if (
      Date.now() - this.lastPresenceAt <
      PRESENCE_INTERVAL - PRESENCE_DUE_SLACK
    )
      return;
    this.sendPresence();
  }

  // Presença do ATENDENTE que não depende do timer da aba. Com a aba escondida
  // há uns minutos o Chrome roda setTimeout no máximo 1×/min: medido em prod
  // (2026-10-19), batida a cada 60 s contra presença de 40 s = o agente some da
  // distribuição 20 s de cada minuto com o painel aberto. O ping que o servidor
  // manda pela conexão a cada 3 s continua chegando, então a batida pega
  // carona nele (no máximo uma a cada PRESENCE_INTERVAL - PRESENCE_DUE_SLACK).
  // Voltar pra aba bate na hora SE a presença já venceu (aba congelada); trocar
  // de aba à toa não gera batida (cada uma roda broadcast_presence da conta
  // inteira no servidor). Opt-in: o widget do site mede presença de CONTATO e
  // não muda.
  beatPresenceOnServerPing() {
    const { monitor } = this.consumer.connection;
    const recordPing = monitor.recordPing.bind(monitor);
    monitor.recordPing = () => {
      recordPing();
      this.beatPresenceIfDue();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.beatPresenceIfDue();
    });
  }

  handleDisconnected() {
    BaseActionCableConnector.isDisconnected = true;
    this.onDisconnected();
    this.initReconnectTimer();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
